import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import {
  Autocomplete,
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Stack,
} from '@mui/material';
import { useGameUtils } from './utilities/GameUtils';
import { useApi } from './services/api';
import { useAuth } from './contexts/AuthContext';
import { useFirebase } from './contexts/FirebaseContext';
import { Avatar } from '@mui/material';
import WaglLogo from './components/wagl-logo';
import iconLink from './assets/images/icon-link.png';
import iconShare from './assets/images/icon-share-native.png';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function GamesInvite() {
  const navigate = useNavigate();
  const { db } = useFirebase();
  const { id } = useParams();
  const { post } = useApi();
  const { currentUser } = useAuth();
  const [game, setGame] = useState({});
  const [userByUsernameOptions, setUserByUsernameOptions] = useState([]);
  const [invitedUsernameUsers, setInvitedUsernameUsers] = useState([]);
  const [usernameSearch, setUsernameSearch] = useState('');
  const [shareLink, setShareLink] = useState('');
  const shareBaseLink = `${process.env.REACT_APP_WAGL_BASE_URL}/games/`;
  const { state } = useLocation();
  const { newGame } = state || {};
  const debouncedUsernameSearch = useDebounce(usernameSearch, 300);
  const [loading, setLoading] = useState(false);
  const { fetchGameById, fetchAllPreviousPlayers } = useGameUtils();
  const [users, setUsers] = useState([]);
  const { isNewGame } = state || {};

  const loadGame = useCallback(
    async (gameId) => {
      const game = await fetchGameById(gameId);
      if (game) {
        setShareLink(`${shareBaseLink}${game.id}`);
        setGame(game);
      }
    },
    [fetchGameById, shareBaseLink]
  );

  useEffect(() => {
    if (id) {
      loadGame(id);
    }
  }, [id, loadGame]);

  useEffect(() => {
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // const handleSplitPaymentChange = (event) => {
  //   setGame((prevData) => ({
  //     ...prevData,
  //     splitCosts: event.target.checked,
  //   }));
  // };

  // Generic search function
  const searchUsers = useCallback(
    async (searchTerm, setOptions, invitedUsers) => {
      if (!searchTerm || searchTerm.length < 3) {
        setOptions([]);
        return;
      }
      const fields = ['username', 'firstName', 'lastName'];
      try {
        const usersRef = collection(db, 'users');

        // Generate common casing variants
        const lower = searchTerm.toLowerCase();
        const upper = searchTerm.toUpperCase();
        const capitalized = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();

        const variants = Array.from(new Set([lower, upper, capitalized])); // remove any duplicates

        // Create multiple queries for each variant

        const queryPromises = fields
          .map((field) =>
            variants.map((variant) =>
              getDocs(
                query(usersRef, where(field, '>=', variant), where(field, '<=', variant + '\uf8ff'))
              )
            )
          )
          .flat();

        const snapshots = await Promise.all(queryPromises);

        // Combine all results and deduplicate by ID
        const usersMap = new Map();
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((doc) => {
            console.log('doc', doc);
            if (!usersMap.has(doc.id)) {
              const user = { id: doc.id, ...doc.data() };
              usersMap.set(doc.id, user);
            }
          });
        });

        // Filter out already invited users
        const users = Array.from(usersMap.values()).filter(
          (user) => !invitedUsers.some((invited) => invited.id === user.id)
        );

        setOptions(users);
      } catch (error) {
        console.error(`Error searching users by: `, error);
      }
    },
    [db]
  );

  const searchUsersByUsername = useCallback(
    (searchTerm) => {
      searchUsers(searchTerm, setUserByUsernameOptions, invitedUsernameUsers);
    },
    [searchUsers, invitedUsernameUsers]
  );

  useEffect(() => {
    searchUsersByUsername(debouncedUsernameSearch);
  }, [debouncedUsernameSearch, searchUsersByUsername]);

  const createInvitations = async () => {
    setLoading(true);
    const invitationsList = invitationsData();

    if (!game?.id) {
      console.error('Game ID is undefined');
      return;
    }

    try {
      // Save the costs before sending invitations.
      const gameRef = doc(db, 'games', game.id);
      await updateDoc(gameRef, game);

      // Invitations are saved in the game document
      // game.invitations = invitationsList;

      // Use the API endpoint to save the invitations
      await post('/invitation/invite-users-to-game', {
        gameID: game.id,
        invitedBy: currentUser.uid,
        userIds: invitationsList,
      });

      if (isNewGame) {
        navigate(`/games/${game.id}/created`);
      } else {
        navigate(`/games/${game.id}`);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error creating invitations:', error);
      throw error;
    }
  };

  const invitationsData = useCallback(() => {
    return invitedUsernameUsers
      .map((user) => {
        if (!user?.uid) {
          console.error('User has no uid:', user);
          return null;
        }
        return user.uid;
      })
      .filter(Boolean);
  }, [invitedUsernameUsers]);

  useEffect(() => {
    const fetchGames = async () => {
      const previousPlayers = await fetchAllPreviousPlayers(currentUser?.uid);
      setUsers(previousPlayers);
    };

    fetchGames();
  }, [currentUser?.uid, fetchAllPreviousPlayers]);

  const handleUserClick = (user) => {
    setInvitedUsernameUsers((prevInvited) => {
      // Check if the user is already invited
      if (!prevInvited.some((invitedUser) => invitedUser.uid === user.uid)) {
        return [...prevInvited, user];
      }
      return prevInvited;
    });
  };

  const handleShareGameInvite = async (shareLink) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my game on Wagl!',
          text: 'Check out this game invite:',
          url: shareLink,
        });
      } catch (err) {
        // User cancelled or error occurred, do nothing
      }
    } else {
      // fallback: copy to clipboard
      navigator.clipboard.writeText(shareLink);
    }
  };

  // const getUserPhotoURL = (uid) => {
  //       const user = dateTimeOptionsWithPhotoURL?.find (user => user?.uid === uid || user?.user === uid);
  //       return user ? user?.photoURL : null;
  // }

  //   const getUserInitial = (uid) => {
  //       const user = userNames?.find (user => user?.uid === uid || user?.user === uid);
  //       return user ? user?.initial : '';
  //   }

  return (
    <Box sx={{ display: 'flex', paddingTop: 2 }}>
      {/* {true && (
        <ReactConfetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={100}
          gravity={0.9} // faster falling
          colors={['#800080', '#9b30ff', '#ba55d3', '#dda0dd', '#e6e6fa']} // purple shades
          recycle={false}
          run={true}
        />
      )} */}
      <Container maxWidth="sm">
        <Typography variant="h2" sx={{ textAlign: 'center' }}>
          Invite Players
        </Typography>

        <Box sx={{ paddingBottom: 1, paddingTop: 2 }}>
          <Typography variant="fieldLabel">Add Previous Players</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, paddingTop: 2 }}>
            {users.map((user, index) => {
              const initial = user?.username?.charAt(0);

              if (user?.photoURL) {
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexDirection: 'column',
                      minWidth: '70px',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleUserClick(user)}
                  >
                    <Avatar
                      alt={user.userName || user.userId}
                      sx={{ bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF' }}
                      src={user?.photoURL}
                    />
                    <Typography
                      color="primary"
                      component="medium"
                      sx={{ fontFamily: 'FactorA Medium', color: '#4C4C4C', fontSize: '0.75rem' }}
                    >
                      {user.username?.slice(0, 10) || 'Unknown'}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: '70px',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleUserClick(user)}
                >
                  <Avatar
                    alt={user.userName || user.userId}
                    sx={{ bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF' }}
                  >
                    <Typography color="primary" component="medium" sx={{ color: '#4C4C4C' }}>
                      {initial?.slice(0, 10) || '?'}
                    </Typography>
                  </Avatar>
                  <Typography
                    color="primary"
                    component="medium"
                    sx={{ color: '#4C4C4C', fontSize: '0.75rem' }}
                  >
                    {user.username?.slice(0, 10) || 'Unknown'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        {/* <Typography variant="fieldLabel">Add Previous Players</Typography> */}
        <Box sx={{ paddingBottom: 1, paddingTop: 2 }}>
          <Typography variant="fieldLabel">Invite Wagl User</Typography>
        </Box>

        <Autocomplete
          multiple
          value={invitedUsernameUsers}
          onChange={(_, newValue) => setInvitedUsernameUsers(newValue)}
          inputValue={usernameSearch}
          onInputChange={(_, newInputValue) => {
            setUsernameSearch(newInputValue);
          }}
          options={userByUsernameOptions}
          getOptionLabel={(option) => option.username || ''}
          noOptionsText=""
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Box
                key={option.id || option.username || index}
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <Box
                  component="span"
                  variant="inviteChip"
                  sx={{
                    backgroundColor: '#8953EF1F',
                    // opacity: 0.12,
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.5,
                    mr: 0.5,
                    fontSize: '0.875rem',
                    color: 'black',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  {...getTagProps({ index })}
                >
                  <WaglLogo />
                  &nbsp;
                  <span style={{ opacity: 1, marginRight: 8 }}>{option.username || ''}</span>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Remove this user from invitedUsernameUsers
                      setInvitedUsernameUsers((prev) =>
                        prev.filter((u) => (u.id || u.username) !== (option.id || option.username))
                      );
                    }}
                    sx={{
                      minWidth: 0,
                      padding: 0,
                      marginLeft: 0.5,
                      color: '#8953EF',
                      fontSize: '1rem',
                      lineHeight: 1,
                      '&:hover': { background: 'none', color: '#D84C6F' },
                    }}
                  >
                    ×
                  </Button>
                </Box>
              </Box>
            ))
          }
          sx={{ height: '50px' }}
        />

        <br />
        <br />
        <Box sx={{ paddingBottom: 1 }}>
          <Typography variant="fieldLabel">Share Game Invite</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField 
            name="shareLink" 
            value={shareLink} 
            disabled={true} 
            sx={{ 
              flex: '1 1 80%',
              '& .MuiInputBase-root': {
                // paddingRight: '40px',
              },
              '& .MuiInputBase-input': {
                // Make sure the input text doesn't overlap the icon
                paddingRight: '50px',
                backgroundImage: `url(${iconLink})`,
                backgroundSize: '20px',
                backgroundPosition: 'right 16px center',
                backgroundRepeat: 'no-repeat',
              },
              '& .MuiInputAdornment-root': {
                marginRight: 0,
              },
            }} 
          />
          <Button
            onClick={handleShareGameInvite}
            sx={{
              // flex: '1 1 20%',
              width: '56px',
              borderRadius: '28px',
              minWidth: 0,
              padding: 0,
              backgroundImage: `url(${iconShare})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: '28px 28px',
              backgroundColor: '#8953EF1F',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(137, 83, 239, 0.08)',
                backgroundImage: `url(${iconShare})`,
              },
            }}
          >
            
          </Button>
        </Stack>

        <br />
        <br />

        {/* <Typography variant="h5">Costs (Optional)</Typography>
        <br />
        <Stack direction="row" spacing={2}>
          <TextField
            label="i.e. green fees, lunch, etc"
            name="costDescription"
            value={game.costDescription}
            onChange={handleFormChange}
            sx={{ flex: '1 1 70%' }}
          />
          <TextField
            name="cost"
            type="number"
            label="Total"
            value={game.cost}
            onChange={handleFormChange}
            sx={{ flex: '1 1 30%' }}
          />
        </Stack>

        <Box sx={{ marginY: '16px' }}>
          <Typography variant="fieldLabel">Split Costs</Typography>
          <Stack direction="row" spacing={4}>
            <Typography>Do you want to split the cost evenly with other players?</Typography>
            <Switch
              checked={game.splitCosts}
              name="splitCosts"
              onChange={(e) => handleSplitPaymentChange(e)}
            />
          </Stack>
        </Box> */}

          <Button
            variant="containedNew"
            sx={{ flex: 1 }}
            onClick={createInvitations}
            disabled={invitedUsernameUsers.length === 0 || loading}
          >
            Invite Players
          </Button>
          <br /><br />
          <Button
            variant="containedNewOutline"
            sx={{
              alignItems: 'center',
              padding: '0px',
              gap: '8px',
              flex: 1,
            }}
            onClick={() => {
              if (isNewGame) {
                navigate(`/games/${id}/created`);
              } else {
                navigate(`/games/${id}`);
              }
            }}
            disabled={loading}
          >
            Done
          </Button>
      </Container>
    </Box>
  );
}

export default GamesInvite;
